import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["MESSAGE", "USER_MESSAGE"];
import { Fragment, useMemo, useCallback } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { AccordionItem } from 'foundations-components/transitional/components/AccordionItem';
import unescapedText from 'I18n/utils/unescapedText';
import { defaultRenderers } from './blocks/defaultRenderers';
import { ArtifactBlock as ArtifactBlockComponent } from './blocks/ArtifactBlock';
import { MessageView } from './MessageView';
import { groupMessages, DEFAULT_STANDALONE_TOOLS } from 'breeze-engine-core/groupMessages';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function hasRenderableBlocks(message, renderers) {
  return message.blocks.some(block => {
    const renderer = renderers[block.type];
    // No renderer → FallbackBlock handles it → renders something → renderable.
    // Renderer with no canRender → conservative: assume renderable.
    if (!(renderer !== null && renderer !== void 0 && renderer.canRender)) return true;
    return renderer.canRender(block);
  });
}
function formatGroupSummary(totalPairs, failedPairs) {
  const base = unescapedText('breezeEngineChatComponents.toolGroup.toolCalls', {
    count: totalPairs
  });
  if (failedPairs > 0) {
    return `${base} ${unescapedText('breezeEngineChatComponents.toolGroup.failed', {
      count: failedPairs
    })}`;
  }
  return base;
}
function countFailed(pairs) {
  return pairs.reduce((n, pair) => {
    var _pair$output;
    return n + (((_pair$output = pair.output) === null || _pair$output === void 0 ? void 0 : _pair$output.type) === 'TEXT' && pair.output.isError ? 1 : 0);
  }, 0);
}
function ToolGroupItem({
  group,
  renderers
}) {
  const summary = formatGroupSummary(group.pairs.length, group.failedPairs);
  const ToolRenderer = renderers.TOOL;
  const renderContent = useCallback(() => {
    const combinedBlock = {
      type: 'TOOL',
      turnId: group.messages[0].id,
      pairs: group.pairs
    };
    return /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "200",
      paddingBlockStart: "200",
      children: ToolRenderer && /*#__PURE__*/_jsx(ToolRenderer, {
        block: combinedBlock
      })
    });
  }, [group, ToolRenderer]);
  return /*#__PURE__*/_jsx(AccordionItem, {
    size: "extra-small",
    flush: true,
    animate: true,
    defaultOpen: false,
    removeContentPadding: true,
    renderContent: renderContent,
    title: /*#__PURE__*/_jsx("span", {
      children: summary
    })
  });
}
export function ChatView({
  messages,
  components,
  onArtifactClick,
  standaloneTools
}) {
  const mergedRenderers = useMemo(() => {
    const withArtifact = Object.assign({}, defaultRenderers, {
      ARTIFACT: ({
        block
      }) => /*#__PURE__*/_jsx(ArtifactBlockComponent, {
        block: block,
        onArtifactClick: onArtifactClick
      })
    });
    if (!components) return withArtifact;
    const overrides = _objectWithoutPropertiesLoose(components, _excluded);
    return Object.assign({}, withArtifact, overrides);
  }, [components, onArtifactClick]);
  const standaloneSet = useMemo(() => standaloneTools && standaloneTools.length > 0 ? new Set([...DEFAULT_STANDALONE_TOOLS, ...standaloneTools]) : DEFAULT_STANDALONE_TOOLS, [standaloneTools]);
  const groups = useMemo(() => groupMessages(messages, standaloneSet), [messages, standaloneSet]);
  return /*#__PURE__*/_jsx(Flex, {
    "data-chat-view": "true",
    flexDirection: "column",
    gap: "400",
    children: groups.map(group => {
      if (group.type !== 'tool-group') {
        const {
          message
        } = group;
        if (message.role === 'ASSISTANT' && !hasRenderableBlocks(message, mergedRenderers)) {
          return null;
        }
        return /*#__PURE__*/_jsx(MessageView, {
          message: message,
          renderers: mergedRenderers,
          MessageWrapper: components === null || components === void 0 ? void 0 : components.MESSAGE,
          UserMessageWrapper: components === null || components === void 0 ? void 0 : components.USER_MESSAGE
        }, message.id);
      }
      const standalonePairs = group.pairs.filter(pair => standaloneSet.has(pair.input.name));
      const nonStandalonePairs = group.pairs.filter(pair => !standaloneSet.has(pair.input.name));
      const nonToolBlocks = group.messages.flatMap(msg => msg.blocks.filter(block => block.type !== 'TOOL'));
      const groupKey = group.messages[0].id;
      const ToolRenderer = mergedRenderers.TOOL;

      // At least one standalone pair: split standalone pairs out to render at full size
      if (standalonePairs.length > 0) {
        return /*#__PURE__*/_jsxs(Fragment, {
          children: [nonStandalonePairs.length > 1 && /*#__PURE__*/_jsx(ToolGroupItem, {
            group: Object.assign({}, group, {
              pairs: nonStandalonePairs,
              failedPairs: countFailed(nonStandalonePairs)
            }),
            renderers: mergedRenderers
          }), nonStandalonePairs.length === 1 && ToolRenderer && /*#__PURE__*/_jsx(ToolRenderer, {
            block: {
              type: 'TOOL',
              turnId: groupKey,
              pairs: nonStandalonePairs
            }
          }), nonToolBlocks.map((block, idx) => {
            const BlockRenderer = mergedRenderers[block.type];
            return BlockRenderer ? /*#__PURE__*/_jsx(BlockRenderer, {
              block: block
            }, `${groupKey}-extra-${idx}`) : null;
          }), standalonePairs.map(pair => ToolRenderer ? /*#__PURE__*/_jsx(ToolRenderer, {
            block: {
              type: 'TOOL',
              turnId: groupKey,
              pairs: [pair]
            }
          }, pair.input.id) : null)]
        }, groupKey);
      }

      // No standalone pairs: original accordion / single-message rendering
      if (group.pairs.length > 1) {
        return /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(ToolGroupItem, {
            group: group,
            renderers: mergedRenderers
          }), nonToolBlocks.map((block, idx) => {
            const BlockRenderer = mergedRenderers[block.type];
            return BlockRenderer ? /*#__PURE__*/_jsx(BlockRenderer, {
              block: block
            }, `${groupKey}-extra-${idx}`) : null;
          })]
        }, groupKey);
      }
      return group.messages.filter(msg => msg.role !== 'ASSISTANT' || hasRenderableBlocks(msg, mergedRenderers)).map(msg => /*#__PURE__*/_jsx(MessageView, {
        message: msg,
        renderers: mergedRenderers,
        MessageWrapper: components === null || components === void 0 ? void 0 : components.MESSAGE,
        UserMessageWrapper: components === null || components === void 0 ? void 0 : components.USER_MESSAGE
      }, msg.id));
    })
  });
}